import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { format, parseISO } from 'date-fns'
import { Button, Form, Table } from 'react-bootstrap'
import { useSortBy, useTable } from 'react-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCaretDown,
  faCaretUp,
  faEye,
  faEyeSlash,
  faTrash
} from '@fortawesome/free-solid-svg-icons'
import ReactPaginate from 'react-paginate'
import { Icon } from '../icon'

/** table to display and edit a page of affiliations (groups) with paging and sorting functionality */
const AffiliationsTable = ({
  affiliations,
  maxOrderNumber,
  page,
  pageCount,
  onPageChange,
  sort,
  onSort,
  onUpdate,
  onDelete
}) => {
  const columns = useMemo(
    () => [
      {
        Header: 'Group Order',
        accessor: 'orderNumber',
        Cell: ({ row, value }) => (
          <Form.Control
            aria-label={`orderNumber for ${row.original.name}`}
            as='select'
            value={value}
            onChange={(e) =>
              onUpdate(row.original.id, 'orderNumber', e.target.value)
            }
          >
            {Array(maxOrderNumber)
              .fill()
              .map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  #{i + 1}
                </option>
              ))}
          </Form.Control>
        )
      },
      {
        Header: 'Group Name',
        accessor: 'name',
        Cell: ({ row, value }) => {
          const [showConfirm, setShowConfirm] = useState(false)
          const [newValue, setNewValue] = useState(value)
          return (
            <>
              <Form.Control
                aria-label={`name for ${row.original.name}`}
                type='text'
                value={newValue}
                onChange={(e) => {
                  setShowConfirm(e.target.value !== value)
                  setNewValue(e.target.value)
                }}
              />
              {showConfirm ? (
                <div className='pt-2'>
                  <Button
                    variant='secondary'
                    onClick={() => onUpdate(row.original.id, 'name', newValue)}
                  >
                    Confirm
                  </Button>{' '}
                  <Button
                    variant='outline-dark'
                    onClick={() => {
                      setNewValue(value)
                      setShowConfirm(false)
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : null}
            </>
          )
        }
      },
      {
        Header: 'Last Modified',
        accessor: 'lastUpdated',
        Cell: ({ value }) => {
          try {
            return value ? format(parseISO(value), 'Pp') : null
          } catch (e) {
            console.error('Error parsing lastUpdated', value, e.stack)
            return null
          }
        }
      },
      {
        Header: 'Date Created',
        accessor: 'dateCreated',
        Cell: ({ value }) => {
          try {
            return value ? format(parseISO(value), 'P') : null
          } catch (e) {
            console.error('Error parsing dateCreated', value, e.stack)
            return null
          }
        }
      },
      {
        Header: 'Actions',
        disableSortBy: true,
        accessor: (row) => row,
        Cell: ({ value }) => {
          const { id, name, hidden } = value
          const text = hidden ? 'Unhide' : 'Hide'
          return (
            <div>
              <Button
                aria-label={`${text} ${name}`}
                variant='link'
                onClick={() => onUpdate(id, 'hidden', !hidden)}
              >
                <Icon icon={value ? faEye : faEyeSlash} size='lg' />
                {text}
              </Button>
              |
              <Button
                aria-label={`Delete ${name}`}
                variant='link'
                onClick={() => onDelete(id)}
              >
                <Icon icon={faTrash} size='lg' />
                Delete
              </Button>
            </div>
          )
        }
      }
    ],
    [onDelete, onUpdate, maxOrderNumber]
  )
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
    setSortBy
  } = useTable(
    {
      columns,
      data: affiliations,
      initialState: { sortBy: sort },
      manualSortBy: true
    },
    useSortBy
  )
  const paginationRef = useRef(null)

  // react-paginate is setting role of UL to 'navigation' (LIs must be in a UL)
  useEffect(() => {
    if (paginationRef.current) {
      const ulElement = paginationRef.current.querySelector('.pagination')
      if (ulElement && ulElement.getAttribute('role') === 'navigation') {
        ulElement.removeAttribute('role')
      }
    }
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setSortBy(sort), [sort])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onSort(sortBy), [sortBy])

  return (
    <>
      <Table
        aria-label='affiliations admin table'
        responsive
        bordered
        hover
        striped
        size='sm'
        className='affiliations-admin-table'
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key, ...groupProps } = headerGroup.getHeaderGroupProps()
            return (
              <tr key={key} {...groupProps}>
                {headerGroup.headers.map((column) => {
                  const { key, ...headerProps } = column.getHeaderProps(
                    column.getSortByToggleProps({ title: undefined })
                  )
                  return (
                    <th key={key} {...headerProps}>
                      {column.render('Header')}
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon
                            icon={faCaretDown}
                            size='lg'
                            className='ml-1'
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faCaretUp}
                            size='lg'
                            className='ml-1'
                          />
                        )
                      ) : (
                        ''
                      )}
                    </th>
                  )
                })}
              </tr>
            )
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row)
            const { key, ...rowProps } = row.getRowProps()
            return (
              <tr key={key} {...rowProps}>
                {row.cells.map((cell) => {
                  const { key, ...cellProps } = cell.getCellProps()
                  return (
                    <td key={key} {...cellProps}>
                      {cell.render('Cell')}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </Table>
      <nav aria-label='Pagination' ref={paginationRef}>
        <ReactPaginate
          pageCount={pageCount}
          pageRangeDisplayed={2}
          marginPagesDisplayed={1}
          previousLabel='<'
          nextLabel='>'
          breakClassName='page-item'
          breakLinkClassName='page-link'
          onPageChange={onPageChange}
          forcePage={page}
          containerClassName='pagination justify-content-center'
          pageClassName='page-item'
          pageLinkClassName='page-link'
          activeClassName='active'
          previousClassName='page-item'
          nextClassName='page-item'
          previousLinkClassName='page-link'
          nextLinkClassName='page-link'
        />
      </nav>
    </>
  )
}

AffiliationsTable.propTypes = {
  /** a page of affiliations (groups) to display */
  affiliations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      orderNumber: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      lastUpdated: PropTypes.string.isRequired,
      dateCreated: PropTypes.string.isRequired,
      hidden: PropTypes.bool.isRequired
    })
  ).isRequired,
  /** the max number (typically total) for ordering of affiliations */
  maxOrderNumber: PropTypes.number.isRequired,
  /** the current page being displayed */
  page: PropTypes.number.isRequired,
  /** total number of pages of data */
  pageCount: PropTypes.number.isRequired,
  /** handler for pagination */
  onPageChange: PropTypes.func.isRequired,
  /** sorting data */
  sort: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      desc: PropTypes.bool
    }).isRequired
  ).isRequired,
  /** handler for sorting */
  onSort: PropTypes.func.isRequired,
  /** handler for data updates */
  onUpdate: PropTypes.func.isRequired,
  /** handler for delete */
  onDelete: PropTypes.func.isRequired
}

export { AffiliationsTable }
